import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ProgressSpinner } from 'primereact/progressspinner';
import ErrorMessage from '../../../../components/messages/ErrorMessage';
import { useCompanyContext } from '../../../../context/CompanyContext';
import { useFormContext } from 'react-hook-form';
import {
  fetchAPOpenPerJob,
  fetchSubmittedPaymentBatches,
} from '../../../../services/accounting/AccountsPayablesService';
import APOpenPerJobTable from './APOpenPerJobTable';
import { fetchAccountsPayablesFilters } from '../APFilters';
import { processPayableListPerJob } from '../apUtil';
import {
  BatchType,
  PaymentBatch,
  PaymentBatchSummary,
} from '../../../../Interfaces/Accounting/AccountsPayables.interfaces';
import { useRolesAccessContext } from '../../../../context/RolesAccessContext';
import { ProcessContextProvider } from '../../../../context/ProcessContext';
import ApprovalHistory from '../../../../components/approvals/ApprovalHistory';

type APopenPerJobTableContainerProps = {
  isDrilldown?: boolean;
  showPerJobRetainage?: boolean;
  showPerJobAmount?: boolean;
  createBatch?: boolean;
  changeCreateBatch?: (data: boolean) => void;
};

const APopenPerJobTableContainer = React.forwardRef<
  HTMLDivElement,
  APopenPerJobTableContainerProps
>(
  (
    {
      isDrilldown,
      showPerJobRetainage,
      showPerJobAmount,
      createBatch,
      changeCreateBatch,
    },
    ref
  ) => {
    const { selectedCompany } = useCompanyContext();
    const { rolesAcess } = useRolesAccessContext();
    const access = rolesAcess?.find(
      (access) => access.report === 'create_payment_batch'
    );
    const { watch } = useFormContext<fetchAccountsPayablesFilters>();
    const [showHistory, setShowHistory] = useState(false);
    const filters = watch();
    const enabled = !!selectedCompany;

    const { data, isFetching, isError } = useQuery({
      queryKey: ['getAPOpenPerJob', selectedCompany, filters],
      queryFn: ({ signal }) => {
        const { reportType, submittedID, ...otherFilters } = filters;

        return fetchAPOpenPerJob(
          {
            ...otherFilters,
            company: selectedCompany!,
            edit: submittedID ? undefined : !!access?.editable,
            draftID: submittedID,
          },
          signal
        );
      },
      refetchOnWindowFocus: false,
      enabled: enabled,
    });

    const submittedPaymentBatches = useQuery({
      queryKey: [
        'getSubmittedPaymentBatches',
        selectedCompany,
        filters.reportType,
      ],
      queryFn: ({ signal }) =>
        fetchSubmittedPaymentBatches(
          selectedCompany!,
          filters.reportType === 'openPayablesPerJob'
            ? BatchType.job
            : BatchType.payType,
          signal
        ),
      refetchOnWindowFocus: false,
      enabled: false,
    });

    const afterApproval = (draftID: string) => {
      submittedPaymentBatches.data?.forEach(
        (draft: PaymentBatchSummary & { disabled?: boolean }) => {
          if (draft.id === draftID) {
            draft.disabled = true;
          }
        }
      );
    };

    if (isFetching || !enabled)
      return (
        <div className="text-center mx-auto mt-3">
          <ProgressSpinner />
        </div>
      );

    if (isError)
      return (
        <ErrorMessage
          content={'Failed to obtain data! Please try again later.'}
        />
      );

    return (
      <ProcessContextProvider
        defaultValues={{
          enableSubmit: data?.batch ? !data.batch.submitted : false,
          enableApprove: data?.batch ? !data.batch.approved : false,
        }}
      >
        {data?.batch && createBatch && (
          <React.Fragment>
            <div
              className="text-18px mx-3 font-bold cursor-pointer totalColor hover:underline flex w-fit"
              onClick={() => setShowHistory(true)}
            >
              Check Draft Approvals
            </div>
            <ApprovalHistory
              show={showHistory}
              hide={() => {
                setShowHistory(false);
              }}
              approvals={data.batch.approvals ?? []}
              lastStep={data.batch.lastStep ?? 0}
            />
          </React.Fragment>
        )}
        <APOpenPerJobTable
          accountsPayables={
            isDrilldown
              ? data.payables
              : processPayableListPerJob(data.payables)
          }
          drilldownStyles={isDrilldown}
          showRetainage={showPerJobRetainage}
          showOpenAmount={showPerJobAmount}
          createBatch={createBatch}
          changeCreateBatch={changeCreateBatch}
          draftID={data.batch?.id}
          defaultBatchPayables={data.batch?.payments.map(
            (payment: PaymentBatch) => {
              const { id, ...otherFields } = payment;
              return otherFields;
            }
          )}
          approvalMode={!!filters.submittedID}
          afterApproval={afterApproval}
        />
      </ProcessContextProvider>
    );
  }
);

export default APopenPerJobTableContainer;
